import React from 'react';
import { Box, Typography } from '@mui/material';
import { framingTarget, useFramingTarget } from '../store/framingTargetStore';
import { clampToSurface } from '../geometry';

const MARKER_SIZE = 16;

interface Props {
  /** Physical surface size, the coordinate basis (feet). */
  surfaceWidth: number;
  surfaceHeight: number;
  /** Rendered size of the surface, in px. */
  width: number;
  height: number;
  /** Radius of the rink's rounded corners (feet); clicks outside them snap onto the ice. */
  cornerRadius?: number;
}

/**
 * Aiming on the virtual surface, in target mode: click or drag
 * to put the target on the ice and the camera turns to it. Uses the same
 * mapping as the entities on it (origin at the centre, y up), so the target
 * is in the arena frame the cameras are calibrated in.
 */
export const RinkTargetLayer: React.FC<Props> = ({
  surfaceWidth,
  surfaceHeight,
  width,
  height,
  cornerRadius = 0,
}) => {
  const { target } = useFramingTarget();

  const place = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const fx = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const fy = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
    const { x, y } = clampToSurface(
      (fx * 2 - 1) * (surfaceWidth / 2),
      (1 - fy * 2) * (surfaceHeight / 2),
      surfaceWidth,
      surfaceHeight,
      cornerRadius,
    );
    framingTarget.place(x, y);
  };

  const markerLeft = target ? ((target.x / (surfaceWidth / 2) + 1) / 2) * width : 0;
  const markerTop = target ? (1 - (target.y / (surfaceHeight / 2) + 1) / 2) * height : 0;

  return (
    <Box
      data-testid="rink-target-layer"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        place(e);
      }}
      onPointerMove={(e) => {
        if (e.buttons & 1) place(e);
      }}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10001,
        cursor: 'crosshair',
        touchAction: 'none',
        // Dims the players a little so the rink reads as a pointer, not a picker.
        backgroundColor: 'rgba(16, 16, 19, 0.2)',
        borderRadius: 1,
      }}
    >
      {!target && (
        <Typography
          sx={{
            position: 'absolute',
            top: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            px: 1,
            borderRadius: 0.5,
            fontSize: 11,
            lineHeight: '18px',
            color: '#fff',
            backgroundColor: 'rgba(16, 16, 19, 0.75)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          Click where the camera should aim
        </Typography>
      )}
      {target && (
        <Box
          sx={{
            position: 'absolute',
            left: markerLeft - MARKER_SIZE / 2,
            top: markerTop - MARKER_SIZE / 2,
            width: MARKER_SIZE,
            height: MARKER_SIZE,
            borderRadius: '50%',
            border: '2px solid #fff',
            backgroundColor: '#ffb020',
            boxShadow: '0 0 0 3px rgba(255, 176, 32, 0.35)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};

export default RinkTargetLayer;
